import type { DraftState, PlannedOrderSettings, WizardFile, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { chooseSampleConfiguration, chooseTargetTaxonomy, withTargetTaxonomy } from './orderSettingsPlan.ts'
import { REJECTION_TEXT } from './itemPlan.ts'

/** The questions whose answers stay changeable until the order is created. */
export type ReopenableKind = 'read-layout' | 'primer-trimming' | 'format-multiplex' | 'target-taxon' | 'processing-type-conflict'

export type GivenAnswer =
  | { kind: 'read-layout'; answer: 'add-mates' | 'merged'; mate: 1 | 2 }
  | { kind: 'primer-trimming'; answer: 'on' | 'removed' }
  | { kind: 'format-multiplex'; answer: 'single' | 'combined' }
  | { kind: 'target-taxon'; answer: PlannedOrderSettings['targetTaxonomy']['value'] }
  | { kind: 'processing-type-conflict'; answer: PlannedOrderSettings['processingType']['value'] }

/** What the lab answered where the files did not tell; changeable only before the order exists, which they configured. */
export const givenAnswers = (draft: DraftState): GivenAnswer[] => {
  const { settings, discovery } = draft
  if (draft.orderId || !settings || !discovery || draft.phase === 'creating' || draft.phase === 'detecting') {
    return []
  }
  const mate = discovery.layout.unmated?.mate ?? 1
  return [
    ...(draft.layoutAnswer ? [{ kind: 'read-layout' as const, answer: 'add-mates' as const, mate }] : []),
    ...(draft.mergedAnswer ? [{ kind: 'read-layout' as const, answer: 'merged' as const, mate }] : []),
    ...(draft.trimmingAnswer ? [{ kind: 'primer-trimming' as const, answer: draft.trimmingAnswer }] : []),
    ...(draft.multiplexAnswer ? [{ kind: 'format-multiplex' as const, answer: draft.multiplexAnswer }] : []),
    ...(settings.targetTaxonomy.source === 'answered' ? [{ kind: 'target-taxon' as const, answer: settings.targetTaxonomy.value }] : []),
    ...(settings.answeredConflict ? [{ kind: 'processing-type-conflict' as const, answer: settings.processingType.value }] : [])
  ]
}

const isAwaitingMate = (file: WizardFile) => file.rejection === REJECTION_TEXT['awaiting-mate']

/* the merged answer made one item per waiting file; before the order exists none of them has gone anywhere */
const unmerged = (draft: DraftState): DraftState => {
  const files = { ...draft.files }
  const groups: Record<string, WizardSampleGroup> = {}
  for (const [key, group] of Object.entries(draft.groups)) {
    if (group.reverse || group.item.status !== 'waiting') {
      groups[key] = group
      continue
    }
    files[group.forward] = { ...files[group.forward], rejection: REJECTION_TEXT['awaiting-layout'], upload: undefined }
  }
  return { ...draft, files, groups, mergedAnswer: undefined }
}

const unplannedFormat = (draft: DraftState, settings: PlannedOrderSettings): PlannedOrderSettings =>
  draft.discovery ? { ...settings, sampleConfiguration: chooseSampleConfiguration(draft.discovery) } : settings

/**
 * The draft as it was before the answer: the question is asked again and the plan follows the new
 * answer. Nothing is written, since the order does not exist yet.
 */
export const reopenAnswer = (draft: DraftState, kind: ReopenableKind): DraftState => {
  const { settings, discovery } = draft
  if (!settings || !discovery || draft.orderId) {
    return draft
  }
  switch (kind) {
    case 'read-layout': {
      const files = Object.fromEntries(
        Object.entries(draft.files).map(([key, file]) => [key, isAwaitingMate(file) ? { ...file, rejection: REJECTION_TEXT['awaiting-layout'] } : file])
      )
      const reopened = { ...draft, files, layoutAnswer: undefined }
      return draft.mergedAnswer ? { ...unmerged(reopened), settings: unplannedFormat(draft, settings) } : reopened
    }
    case 'primer-trimming':
      return { ...draft, trimmingAnswer: undefined, multiplexAnswer: undefined, settings: unplannedFormat(draft, settings) }
    case 'format-multiplex':
      return {
        ...draft,
        multiplexAnswer: undefined,
        settings:
          draft.trimmingAnswer === 'on'
            ? { ...settings, sampleConfiguration: { value: 'SINGLE_SAMPLE_PAIRS', source: 'undetermined', reason: 'primers on the reads, as you said' } }
            : unplannedFormat(draft, settings)
      }
    case 'target-taxon':
      return { ...draft, settings: withTargetTaxonomy(settings, chooseTargetTaxonomy(discovery), discovery) }
    case 'processing-type-conflict': {
      const conflict = settings.answeredConflict
      if (!conflict) {
        return draft
      }
      const asPlanned: PlannedOrderSettings = {
        ...settings,
        answeredConflict: undefined,
        processingType: { value: conflict.preferred, source: 'default', reason: "your organisation's default" },
        processingTypeConflict: undefined
      }
      return { ...draft, settings: withTargetTaxonomy(asPlanned, chooseTargetTaxonomy(discovery), discovery) }
    }
  }
}
